import { useRef, useState, useCallback, useEffect } from "react";
import type { GridPoint, PipeSegment, Fitting, SketchMode, FittingType } from "@/hooks/use-sketch-state";
import { gridToIso, snapToGrid, pipeStrokeWidth, determineAxis } from "@/hooks/use-sketch-state";
import type { IsoAxis } from "@/hooks/use-sketch-state";

const GRID_RANGE = 20;
const DOT_RADIUS = 1.5;
const ELBOW_ARC_RADIUS = 10;

function samePointLocal(a: GridPoint, b: GridPoint) { return a.gx === b.gx && a.gy === b.gy; }

const axisColors: Record<IsoAxis, string> = {
  right: "hsl(150, 70%, 50%)",
  left: "hsl(220, 70%, 60%)",
  up: "hsl(350, 70%, 55%)",
};
const axisLabels: Record<IsoAxis, string> = {
  right: "→ RIGHT",
  left: "← LEFT",
  up: "↑ VERTICAL",
};

const materialColors: Record<string, string> = {
  Copper: "hsl(25, 80%, 55%)",
  Steel: "hsl(220, 15%, 55%)",
  PVC: "hsl(210, 10%, 75%)",
  CPVC: "hsl(45, 70%, 55%)",
  Stainless: "hsl(220, 10%, 70%)",
  "Cast Iron": "hsl(220, 10%, 40%)",
};

const materialHighlights: Record<string, string> = {
  Copper: "hsl(25, 80%, 70%)",
  Steel: "hsl(220, 15%, 70%)",
  PVC: "hsl(210, 10%, 88%)",
  CPVC: "hsl(45, 70%, 70%)",
  Stainless: "hsl(220, 10%, 82%)",
  "Cast Iron": "hsl(220, 10%, 55%)",
};

const fittingSymbols: Record<FittingType, string> = {
  elbow90: "90°",
  elbow45: "45°",
  tee: "T",
  reducer: "RED",
  coupling: "CPL",
  valve: "V",
  cap: "CAP",
};

interface Props {
  segments: PipeSegment[];
  fittings: Fitting[];
  mode: SketchMode;
  drawStart: GridPoint | null;
  onGridClick: (point: GridPoint) => void;
  moveSegment: (id: string, dgx: number, dgy: number) => void;
  moveFitting: (id: string, dgx: number, dgy: number) => void;
}

/** Render a 90° elbow arc between two pipe directions */
function ElbowArc({ cx, cy, inAngle, outAngle, color, highlightColor, strokeWidth }: {
  cx: number; cy: number; inAngle: number; outAngle: number;
  color: string; highlightColor: string; strokeWidth: number;
}) {
  const r = ELBOW_ARC_RADIUS;
  const x1 = cx + r * Math.cos(inAngle);
  const y1 = cy + r * Math.sin(inAngle);
  const x2 = cx + r * Math.cos(outAngle);
  const y2 = cy + r * Math.sin(outAngle);

  // Determine sweep: use the shorter arc
  let angleDiff = outAngle - inAngle;
  while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
  while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
  const sweepFlag = angleDiff > 0 ? 1 : 0;

  return (
    <g>
      {/* Shadow/highlight for 3D effect */}
      <path
        d={`M ${x1} ${y1} A ${r} ${r} 0 0 ${sweepFlag} ${x2} ${y2}`}
        stroke={highlightColor}
        strokeWidth={strokeWidth + 2}
        fill="none"
        strokeLinecap="round"
        opacity={0.5}
      />
      {/* Main arc */}
      <path
        d={`M ${x1} ${y1} A ${r} ${r} 0 0 ${sweepFlag} ${x2} ${y2}`}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
      />
    </g>
  );
}

/** Render a manual fitting (tee, valve, reducer, etc.) */
function ManualFitting({ fit, cx, cy, color }: { fit: Fitting; cx: number; cy: number; color: string }) {
  const symbol = fittingSymbols[fit.type];
  const isValve = fit.type === "valve";
  const isTee = fit.type === "tee";

  return (
    <g>
      {/* Background shape */}
      {isValve ? (
        <>
          <polygon
            points={`${cx - 10},${cy} ${cx},${cy - 8} ${cx + 10},${cy} ${cx},${cy + 8}`}
            fill="hsl(var(--card))"
            stroke={color}
            strokeWidth={2}
          />
          <line x1={cx - 1} y1={cy - 8} x2={cx - 1} y2={cy + 8} stroke={color} strokeWidth={1.5} />
          <line x1={cx + 1} y1={cy - 8} x2={cx + 1} y2={cy + 8} stroke={color} strokeWidth={1.5} />
        </>
      ) : isTee ? (
        <>
          <circle cx={cx} cy={cy} r={9} fill="hsl(var(--card))" stroke={color} strokeWidth={2} />
          {/* T shape inside */}
          <line x1={cx - 5} y1={cy - 3} x2={cx + 5} y2={cy - 3} stroke={color} strokeWidth={1.5} />
          <line x1={cx} y1={cy - 3} x2={cx} y2={cy + 5} stroke={color} strokeWidth={1.5} />
        </>
      ) : (
        <circle cx={cx} cy={cy} r={9} fill="hsl(var(--card))" stroke={color} strokeWidth={2} />
      )}
      {/* Label */}
      {!isValve && !isTee && (
        <text
          x={cx} y={cy + 1}
          textAnchor="middle" dominantBaseline="central"
          fill={color}
          className="text-[7px] font-bold pointer-events-none"
        >
          {symbol}
        </text>
      )}
    </g>
  );
}

const IsometricGrid = ({ segments, fittings, mode, drawStart, onGridClick, moveSegment, moveFitting }: Props) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [hoverPoint, setHoverPoint] = useState<GridPoint | null>(null);
  const [previewEnd, setPreviewEnd] = useState<GridPoint | null>(null);
  const [previewAxis, setPreviewAxis] = useState<IsoAxis | null>(null);
  const [selected, setSelected] = useState<{ kind: "segment" | "fitting"; id: string } | null>(null);
  const [dragInfo, setDragInfo] = useState<{
    kind: "segment" | "fitting";
    id: string;
    startGrid: GridPoint;
    currentDelta: GridPoint;
    moved: boolean;
    snapTarget: GridPoint | null;
    snapAnchor: GridPoint | null;
  } | null>(null);

  // Snap distance in grid units — endpoints within this radius will magnetically connect
  const SNAP_RADIUS_GRID = 1.25;
  // Merge radius (must match `mergeCoincident` in use-sketch-state.ts)
  const MERGE_RADIUS_GRID = 0.5;

  useEffect(() => {
    if (svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      setOffset({ x: rect.width / 2, y: rect.height / 3 });
    }
  }, []);

  const getEventPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left - offset.x) / zoom,
      y: (clientY - rect.top - offset.y) / zoom,
    };
  }, [offset, zoom]);

  const constrainToAxis = useCallback((start: GridPoint, end: GridPoint): GridPoint => {
    const dx = end.gx - start.gx;
    const dy = end.gy - start.gy;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    // Determine which of the 3 isometric axes the cursor is closest to
    // Right axis: move along gx only (dy=0)
    // Left axis: move along gy only (dx=0)
    // Vertical axis: move equal gx & gy (both decrease = up, both increase = down)

    // Project onto each axis and pick the closest
    const rightDist = ady; // distance from right axis (dy=0)
    const leftDist = adx;  // distance from left axis (dx=0)
    // For vertical, the diagonal is dx === dy (same sign)
    const verticalAmount = Math.round((dx + dy) / 2);
    const vertDist = Math.abs(dx - verticalAmount) + Math.abs(dy - verticalAmount);

    if (rightDist <= leftDist && rightDist <= vertDist) {
      return { gx: end.gx, gy: start.gy }; // right axis
    } else if (leftDist <= vertDist) {
      return { gx: start.gx, gy: end.gy }; // left axis
    } else {
      // Vertical axis: equal change in both gx and gy
      return { gx: start.gx + verticalAmount, gy: start.gy + verticalAmount };
    }
  }, []);

  // --- Hit testing in screen-space (post grid->iso transform) ---
  const SEG_HIT_PX = 8;
  const FITTING_HIT_PX = 12;

  const distToSegment = (px: number, py: number, x1: number, y1: number, x2: number, y2: number) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * dx + (py - y1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const cx = x1 + t * dx;
    const cy = y1 + t * dy;
    return Math.hypot(px - cx, py - cy);
  };

  const hitTest = useCallback((sx: number, sy: number): { kind: "segment" | "fitting"; id: string } | null => {
    // Fittings first (smaller, drawn on top)
    for (let i = fittings.length - 1; i >= 0; i--) {
      const f = fittings[i];
      const p = gridToIso(f.position.gx, f.position.gy);
      if (Math.hypot(sx - p.x, sy - p.y) <= FITTING_HIT_PX) return { kind: "fitting", id: f.id };
    }
    for (let i = segments.length - 1; i >= 0; i--) {
      const s = segments[i];
      const a = gridToIso(s.start.gx, s.start.gy);
      const b = gridToIso(s.end.gx, s.end.gy);
      if (distToSegment(sx, sy, a.x, a.y, b.x, b.y) <= SEG_HIT_PX) return { kind: "segment", id: s.id };
    }
    return null;
  }, [segments, fittings]);

  const handlePointerDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (mode === "pan") {
      setIsPanning(true);
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      setPanStart({ x: clientX - offset.x, y: clientY - offset.y });
      return;
    }
    const pos = getEventPos(e);

    if (mode === "select") {
      const hit = hitTest(pos.x, pos.y);
      if (hit) {
        const gp = snapToGrid(pos.x, pos.y, 0, 0);
        setSelected(hit);
        setDragInfo({ kind: hit.kind, id: hit.id, startGrid: gp, currentDelta: { gx: 0, gy: 0 }, moved: false, snapTarget: null, snapAnchor: null });
      } else {
        setSelected(null);
      }
      return;
    }

    const gp = snapToGrid(pos.x, pos.y, 0, 0);
    onGridClick(gp);
  }, [mode, getEventPos, onGridClick, offset, hitTest]);

  // Build the list of snap candidate points (segment endpoints + fitting positions)
  const collectSnapTargets = useCallback((excludeKind: "segment" | "fitting", excludeId: string): GridPoint[] => {
    const pts: GridPoint[] = [];
    const seen = new Set<string>();
    const add = (p: GridPoint) => {
      const k = `${p.gx},${p.gy}`;
      if (!seen.has(k)) { seen.add(k); pts.push(p); }
    };
    for (const s of segments) {
      if (excludeKind === "segment" && s.id === excludeId) continue;
      add(s.start); add(s.end);
    }
    for (const f of fittings) {
      if (excludeKind === "fitting" && f.id === excludeId) continue;
      add(f.position);
    }
    return pts;
  }, [segments, fittings]);

  /**
   * Given the dragged element + raw delta, find the best snap target.
   * Returns the adjusted delta (so a moving endpoint lands exactly on a target),
   * plus the anchor (the point on the dragged element) and target (snapped to).
   */
  const computeSnap = useCallback((
    kind: "segment" | "fitting",
    id: string,
    rawDelta: GridPoint,
  ): { delta: GridPoint; anchor: GridPoint | null; target: GridPoint | null } => {
    const targets = collectSnapTargets(kind, id);
    if (targets.length === 0) return { delta: rawDelta, anchor: null, target: null };

    // Candidate "anchor" points on the dragged element (in their proposed new positions)
    let anchors: { original: GridPoint; proposed: GridPoint }[] = [];
    if (kind === "segment") {
      const seg = segments.find(s => s.id === id);
      if (!seg) return { delta: rawDelta, anchor: null, target: null };
      anchors = [
        { original: seg.start, proposed: { gx: seg.start.gx + rawDelta.gx, gy: seg.start.gy + rawDelta.gy } },
        { original: seg.end,   proposed: { gx: seg.end.gx   + rawDelta.gx, gy: seg.end.gy   + rawDelta.gy } },
      ];
    } else {
      const fit = fittings.find(f => f.id === id);
      if (!fit) return { delta: rawDelta, anchor: null, target: null };
      anchors = [
        { original: fit.position, proposed: { gx: fit.position.gx + rawDelta.gx, gy: fit.position.gy + rawDelta.gy } },
      ];
    }

    let best: { dist: number; anchor: GridPoint; target: GridPoint } | null = null;
    for (const a of anchors) {
      for (const t of targets) {
        const d = Math.hypot(t.gx - a.proposed.gx, t.gy - a.proposed.gy);
        if (d <= SNAP_RADIUS_GRID && (!best || d < best.dist)) {
          best = { dist: d, anchor: a.original, target: t };
        }
      }
    }
    if (!best) return { delta: rawDelta, anchor: null, target: null };

    // Adjust delta so that anchor lands exactly on target
    const adjustedDelta = {
      gx: best.target.gx - best.anchor.gx,
      gy: best.target.gy - best.anchor.gy,
    };
    return { delta: adjustedDelta, anchor: best.anchor, target: best.target };
  }, [collectSnapTargets, segments, fittings]);

  const handlePointerMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (isPanning) {
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      setOffset({ x: clientX - panStart.x, y: clientY - panStart.y });
      return;
    }
    const pos = getEventPos(e);
    const gp = snapToGrid(pos.x, pos.y, 0, 0);
    setHoverPoint(gp);

    if (dragInfo && mode === "select") {
      const rawDgx = gp.gx - dragInfo.startGrid.gx;
      const rawDgy = gp.gy - dragInfo.startGrid.gy;
      const snap = computeSnap(dragInfo.kind, dragInfo.id, { gx: rawDgx, gy: rawDgy });
      const { delta, anchor, target } = snap;
      if (
        delta.gx !== dragInfo.currentDelta.gx ||
        delta.gy !== dragInfo.currentDelta.gy ||
        (target?.gx ?? null) !== (dragInfo.snapTarget?.gx ?? null) ||
        (target?.gy ?? null) !== (dragInfo.snapTarget?.gy ?? null)
      ) {
        setDragInfo({
          ...dragInfo,
          currentDelta: delta,
          moved: dragInfo.moved || (rawDgx !== 0 || rawDgy !== 0),
          snapTarget: target,
          snapAnchor: anchor,
        });
      }
      return;
    }

    if (drawStart && mode === "draw") {
      const constrained = constrainToAxis(drawStart, gp);
      setPreviewEnd(constrained);
      if (!samePointLocal(drawStart, constrained)) {
        setPreviewAxis(determineAxis(drawStart, constrained));
      } else {
        setPreviewAxis(null);
      }
    } else {
      setPreviewAxis(null);
    }
  }, [isPanning, panStart, getEventPos, drawStart, mode, constrainToAxis, dragInfo, computeSnap]);

  const handlePointerUp = useCallback(() => {
    setIsPanning(false);
    if (dragInfo) {
      const { kind, id, currentDelta, moved } = dragInfo;
      if (moved && (currentDelta.gx !== 0 || currentDelta.gy !== 0)) {
        if (kind === "segment") moveSegment(id, currentDelta.gx, currentDelta.gy);
        else moveFitting(id, currentDelta.gx, currentDelta.gy);
      }
      setDragInfo(null);
    }
  }, [dragInfo, moveSegment, moveFitting]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom(z => Math.min(3, Math.max(0.3, z - e.deltaY * 0.001)));
  }, []);

  // Generate grid dots
  const dots = [];
  for (let gx = -GRID_RANGE; gx <= GRID_RANGE; gx++) {
    for (let gy = -GRID_RANGE; gy <= GRID_RANGE; gy++) {
      const { x, y } = gridToIso(gx, gy);
      dots.push(<circle key={`${gx},${gy}`} cx={x} cy={y} r={DOT_RADIUS} className="fill-border" opacity={0.5} />);
    }
  }

  // Collect connection points (endpoints shared by 2+ segments)
  const endpointCounts = new Map<string, { point: GridPoint; count: number }>();
  for (const seg of segments) {
    const sk = `${seg.start.gx},${seg.start.gy}`;
    const ek = `${seg.end.gx},${seg.end.gy}`;
    endpointCounts.set(sk, { point: seg.start, count: (endpointCounts.get(sk)?.count || 0) + 1 });
    endpointCounts.set(ek, { point: seg.end, count: (endpointCounts.get(ek)?.count || 0) + 1 });
  }

  const cursorClass = mode === "draw" ? "cursor-crosshair"
    : mode === "pan" ? (isPanning ? "cursor-grabbing" : "cursor-grab")
    : mode === "delete" ? "cursor-pointer"
    : mode === "select" ? (dragInfo ? "cursor-grabbing" : "cursor-grab")
    : "cursor-default";

  // Live drag delta in grid units (applied visually before commit)
  const dragDelta = dragInfo ? dragInfo.currentDelta : { gx: 0, gy: 0 };
  const isSegDragging = (id: string) => !!dragInfo && dragInfo.kind === "segment" && dragInfo.id === id;
  const isFitDragging = (id: string) => !!dragInfo && dragInfo.kind === "fitting" && dragInfo.id === id;
  // When a segment is being dragged, fittings sitting on its endpoints follow along
  const draggingSeg = dragInfo?.kind === "segment" ? segments.find(s => s.id === dragInfo.id) : null;
  const fittingFollowsDrag = (f: Fitting) => {
    if (!draggingSeg) return false;
    return samePointLocal(f.position, draggingSeg.start) || samePointLocal(f.position, draggingSeg.end);
  };

  // Compute merge-preview pairs: points on the dragged element (in their proposed
  // post-drop position) that will collapse with existing endpoints/fittings.
  type MergePair = { moving: GridPoint; stationary: GridPoint };
  const mergePreview: MergePair[] = (() => {
    if (!dragInfo) return [];
    // Proposed positions of the dragged element's anchor points
    const movingPts: GridPoint[] = [];
    if (dragInfo.kind === "segment") {
      const seg = segments.find(s => s.id === dragInfo.id);
      if (!seg) return [];
      movingPts.push(
        { gx: seg.start.gx + dragDelta.gx, gy: seg.start.gy + dragDelta.gy },
        { gx: seg.end.gx + dragDelta.gx, gy: seg.end.gy + dragDelta.gy },
      );
      // Fittings that follow this segment also count as moving points
      for (const f of fittings) {
        if (samePointLocal(f.position, seg.start) || samePointLocal(f.position, seg.end)) {
          movingPts.push({ gx: f.position.gx + dragDelta.gx, gy: f.position.gy + dragDelta.gy });
        }
      }
    } else {
      const fit = fittings.find(f => f.id === dragInfo.id);
      if (!fit) return [];
      movingPts.push({ gx: fit.position.gx + dragDelta.gx, gy: fit.position.gy + dragDelta.gy });
    }

    // Stationary candidate points (not part of the dragged element)
    const stationary: GridPoint[] = [];
    const seen = new Set<string>();
    const add = (p: GridPoint) => {
      const k = `${p.gx},${p.gy}`;
      if (!seen.has(k)) { seen.add(k); stationary.push(p); }
    };
    for (const s of segments) {
      if (dragInfo.kind === "segment" && s.id === dragInfo.id) continue;
      add(s.start); add(s.end);
    }
    for (const f of fittings) {
      if (dragInfo.kind === "fitting" && f.id === dragInfo.id) continue;
      // If a fitting follows a dragged segment, treat as moving (skip stationary)
      if (dragInfo.kind === "segment" && draggingSeg &&
          (samePointLocal(f.position, draggingSeg.start) || samePointLocal(f.position, draggingSeg.end))) {
        continue;
      }
      add(f.position);
    }

    const pairs: MergePair[] = [];
    for (const m of movingPts) {
      for (const t of stationary) {
        const d = Math.hypot(m.gx - t.gx, m.gy - t.gy);
        if (d <= MERGE_RADIUS_GRID) pairs.push({ moving: m, stationary: t });
      }
    }
    return pairs;
  })();

  return (
    <svg
      ref={svgRef}
      className={`w-full h-full bg-background touch-none select-none ${cursorClass}`}
      onMouseDown={handlePointerDown}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      onMouseLeave={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
      onWheel={handleWheel}
    >
      <g transform={`translate(${offset.x}, ${offset.y}) scale(${zoom})`}>
        {/* Grid dots */}
        {dots}

        {/* Pipe segments with proper thickness and 3D effect */}
        {segments.map(seg => {
          const dragging = isSegDragging(seg.id);
          const sgx = seg.start.gx + (dragging ? dragDelta.gx : 0);
          const sgy = seg.start.gy + (dragging ? dragDelta.gy : 0);
          const egx = seg.end.gx + (dragging ? dragDelta.gx : 0);
          const egy = seg.end.gy + (dragging ? dragDelta.gy : 0);
          const start = gridToIso(sgx, sgy);
          const end = gridToIso(egx, egy);
          const color = materialColors[seg.material] || "hsl(var(--primary))";
          const highlight = materialHighlights[seg.material] || color;
          const sw = pipeStrokeWidth(seg.size);
          const isSelected = selected?.kind === "segment" && selected.id === seg.id;
          return (
            <g key={seg.id} opacity={dragging ? 0.85 : 1}>
              {/* Selection halo */}
              {isSelected && (
                <line
                  x1={start.x} y1={start.y} x2={end.x} y2={end.y}
                  stroke="hsl(var(--primary))" strokeWidth={sw + 6} strokeLinecap="round" opacity={0.35}
                />
              )}
              {/* Pipe shadow for depth */}
              <line
                x1={start.x} y1={start.y + 1.5} x2={end.x} y2={end.y + 1.5}
                stroke="hsl(0,0%,0%)" strokeWidth={sw + 1} strokeLinecap="round" opacity={0.15}
              />
              {/* Main pipe body */}
              <line
                x1={start.x} y1={start.y} x2={end.x} y2={end.y}
                stroke={color} strokeWidth={sw} strokeLinecap="round"
              />
              {/* Top highlight for 3D pipe look */}
              <line
                x1={start.x} y1={start.y - 0.5} x2={end.x} y2={end.y - 0.5}
                stroke={highlight} strokeWidth={sw * 0.35} strokeLinecap="round" opacity={0.6}
              />
              {/* Length label */}
              <text
                x={(start.x + end.x) / 2}
                y={(start.y + end.y) / 2 - sw / 2 - 5}
                textAnchor="middle"
                className="fill-foreground text-[9px] font-mono pointer-events-none"
              >
                {seg.lengthFt.toFixed(1)}&apos;
              </text>
            </g>
          );
        })}

        {/* Connection dots at pipe endpoints (not at fittings) */}
        {Array.from(endpointCounts.values()).map(({ point, count }) => {
          const fittingHere = fittings.some(f => f.position.gx === point.gx && f.position.gy === point.gy);
          if (fittingHere) return null;
          // Hide dots while a segment owning this endpoint is being dragged
          if (draggingSeg && (samePointLocal(draggingSeg.start, point) || samePointLocal(draggingSeg.end, point))) {
            return null;
          }
          const p = gridToIso(point.gx, point.gy);
          const seg = segments.find(s =>
            (s.start.gx === point.gx && s.start.gy === point.gy) ||
            (s.end.gx === point.gx && s.end.gy === point.gy)
          );
          const color = seg ? (materialColors[seg.material] || "hsl(var(--primary))") : "hsl(var(--primary))";
          return (
            <circle
              key={`ep-${point.gx},${point.gy}`}
              cx={p.x} cy={p.y} r={count > 1 ? 4 : 3}
              fill={color}
              stroke="hsl(var(--background))"
              strokeWidth={1}
            />
          );
        })}

        {/* Fittings */}
        {fittings.map(fit => {
          const dragging = isFitDragging(fit.id);
          const follows = fittingFollowsDrag(fit);
          const dx = (dragging || follows) ? dragDelta.gx : 0;
          const dy = (dragging || follows) ? dragDelta.gy : 0;
          const p = gridToIso(fit.position.gx + dx, fit.position.gy + dy);
          const color = materialColors[fit.material || "Copper"] || "hsl(var(--primary))";
          const highlight = materialHighlights[fit.material || "Copper"] || color;
          const isSelected = selected?.kind === "fitting" && selected.id === fit.id;
          const halo = isSelected ? (
            <circle cx={p.x} cy={p.y} r={14} fill="none" stroke="hsl(var(--primary))" strokeWidth={2} opacity={0.6} />
          ) : null;
          const groupOpacity = dragging ? 0.85 : 1;

          // Auto-inserted elbow with arc rendering
          if (fit.auto && fit.type === "elbow90" && fit.inAngle !== undefined && fit.outAngle !== undefined) {
            const sw = pipeStrokeWidth(fit.size);
            return (
              <g key={fit.id} opacity={groupOpacity}>
                {halo}
                <ElbowArc
                  cx={p.x} cy={p.y}
                  inAngle={fit.inAngle} outAngle={fit.outAngle}
                  color={color} highlightColor={highlight}
                  strokeWidth={sw}
                />
              </g>
            );
          }

          // Auto-inserted tee with 3 directional arms
          if (fit.auto && fit.type === "tee" && fit.inAngle !== undefined && fit.outAngle !== undefined && fit.branchAngle !== undefined) {
            const sw = pipeStrokeWidth(fit.size);
            const armLen = ELBOW_ARC_RADIUS + 2;
            const arms = [fit.inAngle, fit.outAngle, fit.branchAngle];
            return (
              <g key={fit.id} opacity={groupOpacity}>
                {halo}
                {/* Draw 3 arms from center */}
                {arms.map((angle, i) => (
                  <line
                    key={i}
                    x1={p.x} y1={p.y}
                    x2={p.x + armLen * Math.cos(angle)}
                    y2={p.y + armLen * Math.sin(angle)}
                    stroke={color}
                    strokeWidth={sw}
                    strokeLinecap="round"
                  />
                ))}
                {/* Center dot */}
                <circle cx={p.x} cy={p.y} r={sw * 0.8} fill={color} />
                <circle cx={p.x} cy={p.y} r={sw * 0.4} fill={highlight} opacity={0.7} />
              </g>
            );
          }

          // Manual fitting
          return (
            <g key={fit.id} opacity={groupOpacity}>
              {halo}
              <ManualFitting fit={fit} cx={p.x} cy={p.y} color={color} />
            </g>
          );
        })}

        {/* Preview line while drawing */}
        {drawStart && previewEnd && mode === "draw" && (() => {
          const p1 = gridToIso(drawStart.gx, drawStart.gy);
          const p2 = gridToIso(previewEnd.gx, previewEnd.gy);
          const color = previewAxis ? axisColors[previewAxis] : "hsl(var(--primary))";
          const label = previewAxis ? axisLabels[previewAxis] : "";
          const dx = Math.abs(previewEnd.gx - drawStart.gx);
          const dy = Math.abs(previewEnd.gy - drawStart.gy);
          const len = previewAxis === "up" ? Math.max(dx, dy) : Math.max(dx, dy);
          return (
            <g>
              <line
                x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke={color} strokeWidth={2.5} strokeDasharray="6 3" opacity={0.8}
              />
              {/* Axis label badge */}
              {previewAxis && !samePointLocal(drawStart, previewEnd) && (
                <g>
                  <rect
                    x={(p1.x + p2.x) / 2 - 28}
                    y={(p1.y + p2.y) / 2 - 20}
                    width={56} height={16} rx={4}
                    fill={color} opacity={0.85}
                  />
                  <text
                    x={(p1.x + p2.x) / 2}
                    y={(p1.y + p2.y) / 2 - 10}
                    textAnchor="middle" dominantBaseline="central"
                    fill="white"
                    className="text-[7px] font-bold pointer-events-none"
                  >
                    {label}
                  </text>
                  {/* Length preview */}
                  <text
                    x={(p1.x + p2.x) / 2}
                    y={(p1.y + p2.y) / 2 - 28}
                    textAnchor="middle"
                    fill={color}
                    className="text-[9px] font-mono font-bold pointer-events-none"
                  >
                    {len}&apos;
                  </text>
                </g>
              )}
              {/* Endpoint dot */}
              <circle cx={p2.x} cy={p2.y} r={4} fill={color} opacity={0.7} />
            </g>
          );
        })()}

        {/* Draw start indicator */}
        {drawStart && mode === "draw" && (() => {
          const p = gridToIso(drawStart.gx, drawStart.gy);
          return (
            <g>
              <circle cx={p.x} cy={p.y} r={7} fill="none" stroke="hsl(var(--primary))" strokeWidth={1.5} opacity={0.6} strokeDasharray="3 2" />
              <circle cx={p.x} cy={p.y} r={3} fill="hsl(var(--primary))" opacity={0.9} />
            </g>
          );
        })()}

        {/* Hover indicator */}
        {hoverPoint && !isPanning && (
          <circle
            cx={gridToIso(hoverPoint.gx, hoverPoint.gy).x}
            cy={gridToIso(hoverPoint.gx, hoverPoint.gy).y}
            r={4} fill="hsl(var(--primary))" opacity={0.3}
          />
        )}

        {/* Snap indicator while dragging — pulses on the target connection point */}
        {dragInfo?.snapTarget && (() => {
          const p = gridToIso(dragInfo.snapTarget.gx, dragInfo.snapTarget.gy);
          return (
            <g pointerEvents="none">
              <circle cx={p.x} cy={p.y} r={11} fill="none" stroke="hsl(var(--primary))" strokeWidth={2} opacity={0.9}>
                <animate attributeName="r" values="9;14;9" dur="0.9s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.9;0.4;0.9" dur="0.9s" repeatCount="indefinite" />
              </circle>
              <circle cx={p.x} cy={p.y} r={4} fill="hsl(var(--primary))" />
              <text
                x={p.x} y={p.y - 16}
                textAnchor="middle"
                fill="hsl(var(--primary))"
                className="text-[8px] font-bold"
              >
                SNAP
              </text>
            </g>
          );
        })()}

        {/* Merge-preview indicators — show every endpoint pair that will collapse on drop */}
        {mergePreview.length > 0 && mergePreview.map((pair, i) => {
          const a = gridToIso(pair.moving.gx, pair.moving.gy);
          const b = gridToIso(pair.stationary.gx, pair.stationary.gy);
          return (
            <g key={`merge-${i}`} pointerEvents="none">
              {/* Connector line between the two points that will merge */}
              <line
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke="hsl(var(--primary))"
                strokeWidth={1.5}
                strokeDasharray="3 2"
                opacity={0.7}
              />
              {/* Pulsing ring on the stationary endpoint */}
              <circle cx={b.x} cy={b.y} r={9} fill="none"
                stroke="hsl(var(--primary))" strokeWidth={2} opacity={0.85}>
                <animate attributeName="r" values="7;12;7" dur="0.8s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.9;0.3;0.9" dur="0.8s" repeatCount="indefinite" />
              </circle>
              {/* Marker on the moving (proposed) point */}
              <circle cx={a.x} cy={a.y} r={5} fill="hsl(var(--primary))" opacity={0.85} />
              <circle cx={a.x} cy={a.y} r={2} fill="hsl(var(--background))" />
            </g>
          );
        })}
      </g>

      {/* Scale indicator */}
      <g transform="translate(16, 16)">
        <rect x={0} y={0} width={80} height={24} rx={4} fill="hsl(var(--card))" stroke="hsl(var(--border))" strokeWidth={1} />
        <text x={40} y={15} textAnchor="middle" className="fill-muted-foreground text-[9px] font-mono">
          1 grid = 1 ft
        </text>
      </g>
    </svg>
  );
};

export default IsometricGrid;
